/**
 * Per-user notification preferences (AB#570 backend, consumed by AB#571 mobile
 * + AB#572 wpp-admin). The SINGLE shared client + layout metadata — both the
 * mobile prefs screen and the admin settings route render from here.
 *
 * Contract (AB#570):
 * - `GET  /notification-prefs` -> `{ data: { prefs: { <event>: {email, push} } } }`
 *   — the EFFECTIVE matrix: stored merged over defaults, all 10 events always
 *   present, security events forced (`{email:true, push:false}`).
 * - `PUT  /notification-prefs` body `{ prefs: { <event>: {email, push} } }`
 *   (partial or full) -> same effective-matrix payload. Unknown event key ->
 *   400; disabling a security event's email -> 400. Server drops values equal
 *   to the default and ignores client values for security events.
 *
 * GET is authoritative for CURRENT VALUES; the grouping/labels/defaults below
 * are the hardcoded layout source of truth (per AB#570 evidence: clients own
 * the layout, backend owns the values).
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** The 8 configurable events (email + push both user-controllable). */
export const CONFIGURABLE_EVENT_KEYS = [
  'deploy_failed',
  'deploy_succeeded',
  'distribution_failed',
  'distribution_sent',
  'form_submission',
  'invite_accepted',
  'payment_failed',
  'plan_changed',
] as const;

/** Security events: email locked ON, no push; client values ignored server-side. */
export const SECURITY_EVENT_KEYS = ['new_login', 'password_changed'] as const;

export type ConfigurableEventKey = (typeof CONFIGURABLE_EVENT_KEYS)[number];
export type SecurityEventKey = (typeof SECURITY_EVENT_KEYS)[number];
export type NotificationEventKey = ConfigurableEventKey | SecurityEventKey;

/** Per-event channel toggles. */
export interface NotificationChannels {
  email: boolean;
  push: boolean;
}

/**
 * The effective matrix as returned by GET — all 10 events present. Widened to
 * `string` keys so a future backend event can't break the type before the UI
 * knows about it; iterate the exported metadata arrays for a stable layout.
 */
export type NotificationPrefs = Record<string, NotificationChannels>;

/** PUT body: partial or full set of event -> channels to change. */
export type NotificationPrefsInput = Partial<Record<NotificationEventKey, NotificationChannels>>;

/** Display + default metadata for one configurable event (layout source of truth). */
export interface NotificationEventMeta {
  key: ConfigurableEventKey;
  label: string;
  description: string;
  /** Layout grouping so the two screens render the same sections. */
  group: 'deploy' | 'distribution' | 'account' | 'billing';
  /** Backend matrix defaults (AB#570) — first-load fallback + optimistic base. */
  default: NotificationChannels;
}

/**
 * The 8 configurable events with labels, grouping and backend defaults.
 * Both #571 (mobile) and #572 (admin) render from this — one source of truth.
 */
export const CONFIGURABLE_EVENTS: NotificationEventMeta[] = [
  {
    key: 'deploy_failed',
    label: 'Deploy failed',
    description: 'A site build or deploy did not complete.',
    group: 'deploy',
    default: { email: true, push: true },
  },
  {
    key: 'deploy_succeeded',
    label: 'Deploy succeeded',
    description: 'A site finished building and deployed.',
    group: 'deploy',
    default: { email: false, push: true },
  },
  {
    key: 'distribution_failed',
    label: 'Distribution failed',
    description: 'A post could not be sent to a platform.',
    group: 'distribution',
    default: { email: true, push: true },
  },
  {
    key: 'distribution_sent',
    label: 'Distribution sent',
    description: 'A post was published to a platform.',
    group: 'distribution',
    default: { email: false, push: false },
  },
  {
    key: 'form_submission',
    label: 'Form submission',
    description: 'A visitor submitted one of your forms.',
    group: 'account',
    default: { email: true, push: true },
  },
  {
    key: 'invite_accepted',
    label: 'Invite accepted',
    description: 'Someone accepted a team invitation.',
    group: 'account',
    default: { email: true, push: false },
  },
  {
    key: 'payment_failed',
    label: 'Payment failed',
    description: 'A subscription payment could not be processed.',
    group: 'billing',
    default: { email: true, push: true },
  },
  {
    key: 'plan_changed',
    label: 'Plan changed',
    description: 'Your subscription plan was updated.',
    group: 'billing',
    default: { email: true, push: false },
  },
];

/** Display metadata for the two locked security events. */
export interface SecurityEventMeta {
  key: SecurityEventKey;
  label: string;
  description: string;
}

export const SECURITY_EVENTS: SecurityEventMeta[] = [
  {
    key: 'new_login',
    label: 'New sign-in',
    description: 'A new device or browser signed in to your account.',
  },
  {
    key: 'password_changed',
    label: 'Password changed',
    description: 'Your account password was changed.',
  },
];

/**
 * First-load default matrix (all 10 events), used before GET resolves and as
 * the base for optimistic updates. Security events are locked `{email, no push}`.
 */
export function defaultNotificationPrefs(): NotificationPrefs {
  const prefs: NotificationPrefs = {};
  for (const e of CONFIGURABLE_EVENTS) prefs[e.key] = { ...e.default };
  for (const e of SECURITY_EVENTS) prefs[e.key] = { email: true, push: false };
  return prefs;
}

export const notificationPrefsApi = {
  /** Effective matrix (stored merged over defaults; all 10 events present). */
  get: () => api.get<ApiResponse<{ prefs: NotificationPrefs }>>('/notification-prefs'),

  /**
   * Persist changed events. Partial or full; only named events change. Rejects
   * unknown keys (400) and disabling a security email (400). Returns the fresh
   * effective matrix.
   */
  put: (prefs: NotificationPrefsInput) =>
    api.put<ApiResponse<{ prefs: NotificationPrefs }>>('/notification-prefs', { prefs }),
};
